import type { OrganizationLocationType } from '@today/types'
import type { OrgLocationDTO } from '@/services/organization.service'
import { COUNTRY_RE, collect, diff, type FieldErrors, optionalText, requiredText, textOrNull } from './form-rules'

/** The location dialog as edited (TO-389 organization-admin-ui REQ-007). Coordinates are text. */
export interface LocationFormValues {
	locationType: OrganizationLocationType | ''
	addressLine1: string
	addressLine2: string
	postalCode: string
	city: string
	province: string
	region: string
	countryCode: string
	latitude: string
	longitude: string
	phone: string
	email: string
}

export const EMPTY_LOCATION_FORM: LocationFormValues = {
	locationType: '',
	addressLine1: '',
	addressLine2: '',
	postalCode: '',
	city: '',
	province: '',
	region: '',
	countryCode: '',
	latitude: '',
	longitude: '',
	phone: '',
	email: '',
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+$/

const coordinate = (text: string, limit: number, key: string): string | null => {
	if (text.trim() === '') return null
	const n = Number(text)
	return Number.isFinite(n) && Math.abs(n) <= limit ? null : key
}

const pairError = (own: string, other: string): string | null =>
	own.trim() === '' && other.trim() !== '' ? 'validation.coordinatesPair' : null

export const locationFormErrors = (v: LocationFormValues): FieldErrors =>
	collect({
		locationType: v.locationType === '' ? 'validation.required' : null,
		addressLine1: requiredText(v.addressLine1, 200),
		addressLine2: optionalText(v.addressLine2, 200),
		city: requiredText(v.city, 100),
		province: optionalText(v.province, 100),
		region: optionalText(v.region, 100),
		countryCode: COUNTRY_RE.test(v.countryCode) ? null : 'validation.country',
		latitude: pairError(v.latitude, v.longitude) ?? coordinate(v.latitude, 90, 'validation.latitude'),
		longitude: pairError(v.longitude, v.latitude) ?? coordinate(v.longitude, 180, 'validation.longitude'),
		email:
			v.email.trim() === '' || (v.email.trim().length <= 254 && EMAIL_RE.test(v.email.trim()))
				? null
				: 'validation.email',
	})

const numberOrNull = (text: string): number | null => (text.trim() === '' ? null : Number(text))

const body = (v: LocationFormValues): Record<string, unknown> => ({
	locationType: v.locationType,
	addressLine1: v.addressLine1.trim(),
	addressLine2: textOrNull(v.addressLine2),
	postalCode: textOrNull(v.postalCode),
	city: v.city.trim(),
	province: textOrNull(v.province),
	region: textOrNull(v.region),
	countryCode: v.countryCode,
	latitude: numberOrNull(v.latitude),
	longitude: numberOrNull(v.longitude),
	phone: textOrNull(v.phone),
	email: textOrNull(v.email),
})

/** The create body: every filled field, no nulls. */
export const buildLocationCreateBody = (v: LocationFormValues): Record<string, unknown> =>
	Object.fromEntries(Object.entries(body(v)).filter(([, value]) => value !== null))

export const locationToForm = (l: OrgLocationDTO): LocationFormValues => ({
	locationType: l.locationType,
	addressLine1: l.addressLine1,
	addressLine2: l.addressLine2 ?? '',
	postalCode: l.postalCode ?? '',
	city: l.city,
	province: l.province ?? '',
	region: l.region ?? '',
	countryCode: l.countryCode,
	latitude: l.latitude === null ? '' : String(l.latitude),
	longitude: l.longitude === null ? '' : String(l.longitude),
	phone: l.phone ?? '',
	email: l.email ?? '',
})

/** Only the changed fields; a cleared optional is sent as null. */
export const buildLocationPatch = (original: OrgLocationDTO, v: LocationFormValues): Record<string, unknown> =>
	diff(body(locationToForm(original)), body(v))
